import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  People as PeopleIcon,
  Agriculture as AgricultureIcon,
  AdminPanelSettings as AdminIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { formatDate } from '../../utils/dateUtils';

/**
 * Composant pour afficher le tableau de bord principal
 */
const Dashboard = ({ stats, adminCount, loading, error, successMessage, setActiveSection }) => {
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: { xs: 2, sm: 3 }, color: '#014527', fontWeight: 'bold', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
        Tableau de bord
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Carte des statistiques totales - Cliquable */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)', cursor: 'pointer' }
            }}
            onClick={() => setActiveSection('farmers')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PeopleIcon sx={{ color: '#014527', mr: 1 }} />
                  <Typography variant="h6">Agriculteurs</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#014527' }}>
                  {stats.totalFarmers}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total des agriculteurs validés
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Carte des vérifications en attente */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)', cursor: 'pointer' }
            }}
            onClick={() => setActiveSection('pending')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AgricultureIcon sx={{ color: '#f59e0b', mr: 1 }} />
                  <Typography variant="h6">En attente</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                  {stats.pendingVerifications}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  En attente de validation
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Carte des demandes d'administrateurs */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)', cursor: 'pointer' }
            }}
            onClick={() => setActiveSection('administrators')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AdminIcon sx={{ color: '#2563eb', mr: 1 }} />
                  <Typography variant="h6">Administrateurs</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#2563eb' }}>
                  {adminCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total des administrateurs
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)', cursor: 'pointer' }
            }}
            onClick={() => setActiveSection('diagnostics')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AssignmentIcon sx={{ color: '#7c3aed', mr: 1 }} />
                  <Typography variant="h6">Diagnostics</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#7c3aed' }}>
                  {stats.newDiagnostics || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Nouveaux diagnostics
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Liste des inscriptions récentes */}
          <Grid item xs={12}>
            <Paper sx={{ 
              p: 3, 
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Inscriptions récentes
              </Typography>
              <List>
                {stats.recentRegistrations.length > 0 ? (
                  stats.recentRegistrations.map((farmer, index) => (
                    <React.Fragment key={farmer.id}>
                      <ListItem>
                        <ListItemIcon>
                          <AgricultureIcon sx={{ color: '#014527' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={farmer.name} 
                          secondary={`Email: ${farmer.email} | Téléphone: ${farmer.phone || 'Non spécifié'}`}
                        />
                          <Typography variant="caption" color="text.secondary">
                              {formatDate(farmer.createdAt)}
                          </Typography>
                      </ListItem>
                      {index < stats.recentRegistrations.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="Aucune inscription récente" />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Dashboard;
